import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Stage, Layer, Image as KonvaImage, Text as KonvaText, Rect as KonvaRect } from 'react-konva';
import { orderAPI, quoteAPI, invoiceAPI, contactAPI } from '../../services/api';
import useNotifications from '../../hooks/useNotifications';
import { Button, Modal, StatusBadge, Pagination, ConfirmDialog, EmptyState } from '../../components/common';
import { toast } from 'react-toastify';

/* ════════════════════════════════════════════════════
   ORDERS PAGE
   ════════════════════════════════════════════════════ */
/* ════════════════════════════════════════════════════
   ORDERS PAGE — Commandes studio + Devis rapides
   ════════════════════════════════════════════════════ */
/* ── Mini-viewer Konva : recrée exactement le studio ── */
const KonvaElement = ({ el }) => {
  const [elImg, setElImg] = React.useState(null);
  React.useEffect(() => {
    const src = el.type === 'image' ? el.src : null;
    if (!src) return;
    const image = new window.Image();
    image.onload  = () => setElImg(image);
    image.onerror = () => setElImg(null);
    image.src = src;
  }, [el.src, el.type]);
  if (el.type === 'text') {
    return (
      <KonvaText
        x={el.x || 0} y={el.y || 0}
        text={el.text || ''}
        fontSize={el.fontSize || 24}
        fontFamily={el.fontFamily || 'Arial'}
        fill={el.fill || '#000000'}
        rotation={el.rotation || 0}
        align={el.align || 'left'}
        fontStyle={el.fontStyle || ''}
        listening={false}
      />
    );
  }
  if (el.type === 'image' && elImg) {
    return (
      <KonvaImage
        x={el.x || 0} y={el.y || 0}
        image={elImg}
        width={el.width || 100}
        height={el.height || 100}
        rotation={el.rotation || 0}
        listening={false}
      />
    );
  }
  return null;
};

const MockupImage = ({ url, width, height }) => {
  const [img, setImg] = React.useState(null);
  React.useEffect(() => {
    if (!url) return;
    const image = new window.Image();
    // Charger sans crossOrigin pour l'affichage (pas d'export canvas)
    image.onload  = () => setImg(image);
    image.onerror = () => setImg(null);
    image.src = url;
  }, [url]);
  return img ? <KonvaImage image={img} x={0} y={0} width={width} height={height} listening={false} /> : null;
};

const DesignFaceViewer = ({ face, elements, mockupInfo, maxWidth = 360 }) => {
  const info = mockupInfo?.[face] || {};
  const cw   = info.canvas_w  || 600;
  const ch   = info.canvas_h  || 600;
  const pz   = info.print_zone || { x: 150, y: 100, w: 200, h: 200 };
  const scale = Math.min(maxWidth / cw, 1);

  return (
    <div style={{ display: 'inline-block', boxShadow: '0 2px 12px rgba(0,0,0,.1)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <Stage width={cw * scale} height={ch * scale} scaleX={scale} scaleY={scale}>
        <Layer>
          <KonvaRect x={0} y={0} width={cw} height={ch} fill="#ffffff" />
          {info.image_url && <MockupImage url={info.image_url} width={cw} height={ch} />}
          <KonvaRect
            x={pz.x} y={pz.y} width={pz.w} height={pz.h}
            fill="transparent" stroke="#3b82f6" strokeWidth={1.5}
            dash={[6, 4]} listening={false}
          />
          {(elements || []).map((el, i) => <KonvaElement key={i} el={el} />)}
        </Layer>
      </Stage>
    </div>
  );
};

export const AdminOrdersPage = () => {
  const [tab,         setTab]        = useState('studio');
  const [orders,      setOrders]     = useState([]);
  const [quickQuotes, setQuickQuotes]= useState([]);
  const [meta,        setMeta]       = useState(null);
  const [page,        setPage]       = useState(1);
  const [search,      setSearch]     = useState('');
  const [status,      setStatus]     = useState('');
  const [loading,     setLoading]    = useState(true);
  const [detail,      setDetail]     = useState(null);
  const [confirm,     setConfirm]    = useState(null);
  const [confirmQQ,   setConfirmQQ]  = useState(null);
  const [qqError,     setQqError]    = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);

  // Compteurs non lus pour pastilles — combinés local + global
  const { counts: notifCounts, refresh: refreshNotifs } = useNotifications();
  const [ordersUnread, setOrdersUnread] = useState(0);
  const [qqUnread,     setQqUnread]     = useState(0);

  // Synchroniser avec les compteurs globaux à chaque refresh
  React.useEffect(() => {
    setOrdersUnread(notifCounts.orders_new || 0);
    setQqUnread(notifCounts.quick_quotes_new || 0);
  }, [notifCounts]);

  /* ── Chargement commandes studio ── */
  const fetchOrders = useCallback(() => {
    setLoading(true);
    const p = { page, per_page: 20 };
    if (search) p.search = search;
    if (status) p.status = status;
    orderAPI.list(p)
      .then(({ data }) => {
        const list = data.data || [];
        setOrders(list);
        setMeta(data.meta);
        setOrdersUnread(list.filter(o => !o.is_read).length);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search, status]);

  /* ── Chargement devis rapides ── */
  const fetchQuickQuotes = useCallback(() => {
    setLoading(true);
    setQqError(false);
    contactAPI.quickQuotes()
      .then(({ data }) => {
        const list = Array.isArray(data.data) ? data.data : [];
        setQuickQuotes(list);
        setQqUnread(list.filter(q => !q.is_read).length);
      })
      .catch(() => setQqError(true))
      .finally(() => setLoading(false));
  }, []);

  // Chargement initial des deux pour avoir les compteurs corrects dès l'ouverture
  useEffect(() => {
    fetchOrders();
    fetchQuickQuotes();
  }, []); // eslint-disable-line

  // Rechargement au changement d'onglet
  useEffect(() => {
    if (tab === 'studio') fetchOrders();
    else fetchQuickQuotes();
  }, [tab, fetchOrders, fetchQuickQuotes]);

  /* ── Supprimer un devis rapide ── */
  const deleteQuickQuote = async () => {
    try {
      await contactAPI.deleteQuickQuote(confirmQQ);
      toast.success('Demande supprimée');
      setConfirmQQ(null);
      fetchQuickQuotes();
      refreshNotifs();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  /* ── Ouvrir détail → marquer comme lu automatiquement ── */
  const openDetail = async (item, type) => {
    setDetail({ ...item, _type: type });
    if (!item.is_read) {
      try {
        if (type === 'studio') {
          await orderAPI.markRead(item.id);
          setOrders(prev => prev.map(o => o.id === item.id ? { ...o, is_read: 1 } : o));
          setOrdersUnread(prev => Math.max(0, prev - 1));
        } else {
          await contactAPI.markQuoteRead(item.id);
          setQuickQuotes(prev => prev.map(q => q.id === item.id ? { ...q, is_read: 1 } : q));
          setQqUnread(prev => Math.max(0, prev - 1));
        }
      } catch { /* silencieux */ }
      refreshNotifs(); // mettre à jour les badges sidebar
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await orderAPI.updateStatus(id, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchOrders();
      if (detail?.id === id) setDetail(prev => ({ ...prev, status: newStatus }));
    } catch { toast.error('Erreur'); }
  };

  const createQuoteFromQQ = async (qq) => {
    try {
      const { data } = await quoteAPI.create({
        client_snapshot: { company: qq.company || '', email: qq.email, phone: qq.phone || '', first_name: qq.name || '' },
        items: [{ description: `${qq.product_type || 'Produit'} — ${qq.technique || 'Technique à définir'} — Qté : ${qq.quantity || '?'}`, quantity: parseInt(qq.quantity) || 1, unit_price: 0, discount: 0 }],
        notes: qq.details || '',
        status: 'draft',
      });
      toast.success('Devis créé ✓');
      setDetail(null);
      Navigate('/admin/quotes/' + data.data?.id + '/edit');
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur création devis'); }
  };

  const createQuote = async (orderId) => {
    try {
      const { data } = await quoteAPI.fromOrder({ order_id: orderId });
      toast.success(`Devis ${data.data.reference} créé ✓`);
      fetchOrders();
      setDetail(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
  };

  const deleteOrder = async (id) => {
    try { await orderAPI.delete(id); toast.success('Supprimé'); fetchOrders(); }
    catch { toast.error('Erreur'); }
    setConfirm(null);
  };

  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isAllSelected = (list) => list.length > 0 && list.every(i => selectedIds.has(i.id));
  const toggleAll = (list) => setSelectedIds(isAllSelected(list) ? new Set() : new Set(list.map(i => i.id)));
  const bulkDelete = async () => {
    let ok = 0;
    if (tab === 'studio') {
      for (const id of selectedIds) { try { await orderAPI.delete(id); ok++; } catch {} }
      fetchOrders();
    } else {
      for (const id of selectedIds) { try { await contactAPI.deleteQuickQuote(id); ok++; } catch {} }
      fetchQuickQuotes(); refreshNotifs();
    }
    toast.success(`${ok} élément(s) supprimé(s)`);
    setSelectedIds(new Set()); setBulkConfirm(false);
  };

  const statusLabels = { new: 'Nouveau', processing: 'En cours', quoted: 'Devisé', cancelled: 'Annulé' };
  const qqStatusLabels = { new: 'Nouveau', processing: 'En cours', done: 'Traité' };
  const qqStatusBadge  = { new: 'badge-warning', processing: 'badge-info', done: 'badge-success' };

  const Badge = ({ count }) => count > 0 ? (
    <span style={{ background: 'var(--brand-accent)', color: 'white', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 'var(--radius-full)', minWidth: 18, textAlign: 'center', lineHeight: '16px' }}>
      {count}
    </span>
  ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Commandes & Demandes</h1><p className="admin-page-subtitle">Demandes reçues depuis le site</p></div>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 4, padding: '0 var(--space-xl)', borderBottom: '1px solid var(--border)', marginBottom: 'var(--space-md)' }}>
        {[
          { key: 'studio',     label: 'Commandes studio',  count: ordersUnread },
          { key: 'quickquotes',label: 'Devis rapides',      count: qqUnread },
        ].map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setPage(1); setSelectedIds(new Set()); }}
            style={{ padding: '10px 20px', fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'none',
              borderBottom: tab === t.key ? '3px solid var(--brand-accent)' : '3px solid transparent',
              color: tab === t.key ? 'var(--brand-accent)' : 'var(--text-secondary)', marginBottom: -1, transition: 'all .15s',
              display: 'flex', alignItems: 'center', gap: 8 }}>
            {t.label}
            <Badge count={t.count} />
          </button>
        ))}
      </div>

      <div className="admin-page-content">

        {/* ── Commandes studio ── */}
        {tab === 'studio' && (
          <div className="admin-card">
            <div className="admin-card-header">
              <div className="admin-filters">
                <div className="search-bar">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input className="form-input" placeholder="Référence, email…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ paddingLeft: 36 }} />
                </div>
                <select className="filter-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
                  <option value="">Tous les statuts</option>
                  {Object.entries(statusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>

            {loading ? <div className="page-loader"><div className="spinner" /></div> :
              !orders.length ? <EmptyState icon="🎨" title="Aucune commande studio" /> :
              <table className="data-table">
                <thead><tr><th style={{width:36}}><input type="checkbox" checked={isAllSelected(orders)} onChange={()=>toggleAll(orders)} /></th><th>Référence</th><th>Contact</th><th>Produit</th><th>Prix estimé</th><th>Statut</th><th>Date</th><th>Actions</th></tr></thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id} style={{ background: !o.is_read ? 'rgba(59,130,246,.03)' : '' }}>
                      <td><input type="checkbox" checked={selectedIds.has(o.id)} onChange={()=>toggleSelect(o.id)} /></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {!o.is_read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--brand-accent)', flexShrink: 0 }} />}
                          <code style={{ fontSize: 12, background: 'var(--bg-page)', padding: '2px 6px', borderRadius: 4 }}>{o.reference}</code>
                        </div>
                      </td>
                      <td><div style={{ fontWeight: 600 }}>{o.contact_name}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{o.contact_email}</div></td>
                      <td style={{ fontSize: 13 }}>{o.product_snapshot?.name || '—'}</td>
                      <td style={{ fontWeight: 700, color: 'var(--brand-accent)' }}>{parseFloat(parseFloat(o.estimated_price) || 0).toFixed(2)} €</td>
                      <td><StatusBadge status={o.status} /></td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(o.created_at).toLocaleDateString('fr-FR')}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button size="sm" variant="secondary" onClick={() => openDetail(o, 'studio')}>Voir</Button>
                          <Button size="sm" variant="ghost" onClick={() => setConfirm(o.id)}>🗑</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
            <div style={{ padding: 'var(--space-md) var(--space-lg)', borderTop: '1px solid var(--border)' }}>
              <Pagination meta={meta} onPageChange={setPage} />
            </div>
          </div>
        )}

        {/* ── Devis rapides ── */}
        {tab === 'quickquotes' && (
          <div className="admin-card">
            {loading ? <div className="page-loader"><div className="spinner" /></div> :
              qqError ? (
                <div style={{ padding: 'var(--space-xl)', textAlign: 'center' }}>
                  <p style={{ fontWeight: 700, marginBottom: 16 }}>⚠️ Impossible de charger les devis rapides</p>
                  <Button variant="primary" onClick={fetchQuickQuotes}>🔄 Réessayer</Button>
                </div>
              ) :
              !quickQuotes.length ? <EmptyState icon="📋" title="Aucun devis rapide" description="Les demandes via /devis-rapide apparaîtront ici." /> :
              <table className="data-table">
                <thead><tr><th>Nom</th><th>Email</th><th>Téléphone</th><th>Entreprise</th><th>Type produit</th><th>Quantité</th><th>Statut</th><th>Date</th><th>Actions</th></tr></thead>
                <tbody>
                  {quickQuotes.map(q => (
                    <tr key={q.id} style={{ background: !q.is_read ? 'rgba(59,130,246,.03)' : '' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {!q.is_read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--brand-accent)', flexShrink: 0 }} />}
                          <span style={{ fontWeight: 700 }}>{q.name}</span>
                        </div>
                      </td>
                      <td><a href={`mailto:${q.email}`} style={{ color: 'var(--brand-accent)', fontSize: 13 }}>{q.email}</a></td>
                      <td style={{ fontSize: 13 }}>{q.phone || '—'}</td>
                      <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{q.company || '—'}</td>
                      <td style={{ fontSize: 13 }}>{q.product_type || '—'}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{q.quantity || '—'}</td>
                      <td><span className={`badge ${qqStatusBadge[q.status] || 'badge-neutral'}`}>{qqStatusLabels[q.status] || q.status}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{new Date(q.created_at).toLocaleDateString('fr-FR')}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button size="sm" variant="secondary" onClick={() => openDetail(q, 'quickquote')}>Voir</Button>
                          <a href={`mailto:${q.email}?subject=Suite à votre demande de devis`} className="btn btn-primary btn-sm" title="Répondre">📧</a>
                          <Button size="sm" variant="ghost" onClick={() => setConfirmQQ(q.id)} title="Supprimer">🗑</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </div>
        )}
      </div>

      {/* ── Modal détail commande studio ── */}
      <Modal isOpen={!!detail && detail._type === 'studio'} onClose={() => setDetail(null)}
        title={`Commande — ${detail?.reference || ''}`} size="lg">
        {detail && detail._type === 'studio' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            {/* Infos contact + commande */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                ['Nom',         detail.contact_name],
                ['Email',       detail.contact_email],
                ['Téléphone',   detail.contact_phone || '—'],
                ['Entreprise',  detail.contact_company || '—'],
                ['Produit',     detail.product_snapshot?.name || '—'],
                ['Référence',   detail.product_snapshot?.reference || '—'],
                ['Couleur',     detail.design_data?.color?.name || '—'],
                ['Prix estimé', `${parseFloat(detail.estimated_price || 0).toFixed(2)} €`],
              ].map(([l, v]) => (
                <div key={l} style={{ background: 'var(--bg-page)', padding: '8px 12px', borderRadius: 'var(--radius-sm)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 3 }}>{l}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{v}</div>
                </div>
              ))}
            </div>

            {/* ── Rendu studio par face (Konva live) ── */}
            {detail.design_data?.faces && (() => {
              const faceLabels  = { front:'Avant', back:'Arrière', left:'Gauche', right:'Droite' };
              const mockupInfo  = detail.design_data?.mockup_info || {};
              const facesWithEl = Object.entries(detail.design_data.faces).filter(([, els]) => Array.isArray(els));
              if (!facesWithEl.length) return null;
              return (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 12 }}>
                    🎨 Personnalisation du client
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {facesWithEl.map(([face, els]) => (
                      <div key={face} style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-accent)' }}>
                          {faceLabels[face] || face}
                          {els.length > 0 && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>({els.length} élément{els.length > 1 ? 's' : ''})</span>}
                        </div>
                        <DesignFaceViewer
                          face={face}
                          elements={els}
                          mockupInfo={mockupInfo}
                          maxWidth={300}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>📐 Configuration</div>
                {detail.design_data?.color && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: detail.design_data.color.hex || '#ccc', border: '1px solid var(--border)', flexShrink: 0 }} />
                    <span>Couleur : <strong>{detail.design_data.color.name || detail.design_data.color.hex}</strong></span>
                  </div>
                )}
                {detail.design_data?.total_quantity > 0 && (
                  <div style={{ fontSize: 13 }}>Quantité totale : <strong>{detail.design_data.total_quantity} pièces</strong></div>
                )}
                {/* ── Fichiers originaux uploadés par le client ── */}
                {detail.design_data?.faces && (() => {
                  const faceLabels = { front:'Avant', back:'Arrière', left:'Gauche', right:'Droite' };
                  // Collecter tous les éléments image avec leur src base64
                  const clientImages = [];
                  Object.entries(detail.design_data.faces).forEach(([face, els]) => {
                    if (!Array.isArray(els)) return;
                    els.forEach((el, i) => {
                      if (el.type === 'image' && el.src) {
                        clientImages.push({ face, label: faceLabels[face] || face, src: el.src, idx: i });
                      }
                    });
                  });
                  if (!clientImages.length) return null;
                  return (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 10 }}>
                        🖼 Visuels uploadés par le client
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {clientImages.map((item, i) => (
                          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                            <img src={item.src} alt={`Logo ${item.label}`}
                              style={{ width: 120, height: 120, objectFit: 'contain', background: '#f8f8f8', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}
                            />
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                              {item.label}
                            </div>
                            <a
                              href={item.src}
                              download={`logo-${item.face}-${i + 1}.png`}
                              style={{ fontSize: 11, color: 'var(--brand-accent)', fontWeight: 600, textDecoration: 'none', padding: '3px 8px', border: '1px solid var(--brand-accent)', borderRadius: 'var(--radius-sm)' }}
                            >
                              ⬇ Télécharger
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Résumé des éléments par face */}
                {detail.design_data?.face_summaries && Object.keys(detail.design_data.face_summaries).length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
                      📋 Détail de la personnalisation
                    </div>
                    {Object.entries(detail.design_data.face_summaries).map(([face, summary]) => (
                      <div key={face} style={{ marginBottom: 8, padding: '10px 12px', background: 'var(--bg-page)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--brand-accent)', marginBottom: 6 }}>
                          {summary.label} — {summary.count} élément(s)
                        </div>
                        {summary.elements.map((el, i) => (
                          <div key={i} style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8, marginBottom: 2 }}>
                            {el.type === 'text'
                              ? `✏️ "${el.text}" — ${el.fontFamily || 'Arial'} ${el.fontSize || 24}px — couleur ${el.fill || '#000'}`
                              : `🖼 Image positionnée en x:${el.x || 0} y:${el.y || 0}`}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {detail.notes && (
                  <div style={{ marginTop: 4, padding: '8px 10px', background: 'var(--bg-page)', borderRadius: 'var(--radius-sm)', fontSize: 13, lineHeight: 1.6, borderLeft: '3px solid var(--brand-accent)' }}>
                    <strong>Notes :</strong> {detail.notes}
                  </div>
                )}
            </div>
            {detail.quantities && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Quantités par taille</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.entries(detail.quantities).map(([size, qty]) => parseInt(qty) > 0 && (
                    <div key={size} style={{ padding: '4px 12px', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
                      <strong>{size}</strong> × {qty}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Statut :</span>
              <select className="filter-select" value={detail.status} onChange={e => updateStatus(detail.id, e.target.value)}>
                {Object.entries(statusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {detail.status !== 'quoted' && <Button variant="primary" onClick={() => createQuote(detail.id)}>Créer un devis</Button>}
            </div>
            <a href={`mailto:${detail.contact_email}?subject=Suite à votre commande ${detail.reference}`} className="btn btn-primary btn-md">📧 Répondre par email</a>
          </div>
        )}
      </Modal>

      {/* ── Modal détail devis rapide ── */}
      <Modal isOpen={!!detail && detail._type === 'quickquote'} onClose={() => setDetail(null)}
        title={`Devis rapide — ${detail?.name || ''}`} size="lg">
        {detail && detail._type === 'quickquote' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>

            {/* Infos contact */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                ['Nom',            detail.name],
                ['Email',          detail.email],
                ['Téléphone',      detail.phone || '—'],
                ['Entreprise',     detail.company || '—'],
              ].map(([l, v]) => (
                <div key={l}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{l}</div>
                  <div style={{ fontSize: 14 }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Détails projet */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, background: 'var(--bg-page)', borderRadius: 'var(--radius-md)', padding: 14 }}>
              {[
                ['Type de produit',  detail.product_type || '—'],
                ['Technique',        detail.technique    || '—'],
                ['Quantité',         detail.quantity     || '—'],
                ['Budget',           detail.budget       || '—'],
                ['Délai',            detail.deadline     || '—'],
                ['Date demande',     new Date(detail.created_at).toLocaleDateString('fr-FR')],
              ].map(([l, v]) => (
                <div key={l}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 3 }}>{l}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{v}</div>
                </div>
              ))}
            </div>

            {detail.details && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Description du projet</div>
                <div style={{ padding: 14, background: 'var(--bg-page)', borderRadius: 'var(--radius-sm)', fontSize: 13, lineHeight: 1.75, whiteSpace: 'pre-wrap', borderLeft: '3px solid var(--brand-accent)' }}>{detail.details}</div>
              </div>
            )}

            {/* Gestion statut */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Statut :</span>
              <select
                className="filter-select"
                value={detail.status}
                onChange={async e => {
                  const newStatus = e.target.value;
                  try {
                    await contactAPI.updateQuoteStatus(detail.id, { status: newStatus });
                    setDetail(d => ({ ...d, status: newStatus }));
                    setQuickQuotes(prev => prev.map(q => q.id === detail.id ? { ...q, status: newStatus } : q));
                    toast.success('Statut mis à jour ✓');
                  } catch { toast.error('Erreur'); }
                }}
              >
                {[['new','🆕 Nouveau'],['processing','⚙️ En traitement'],['done','✅ Traité'],['cancelled','❌ Annulé']].map(([v,l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
              <Button variant="primary" onClick={() => createQuoteFromQQ(detail)}>
                📋 Créer un devis
              </Button>
            </div>

            <a
              href={`mailto:${detail.email}?subject=Votre demande de devis — ${detail.product_type || 'Artimarq'}&body=Bonjour ${detail.name},%0A%0ANous avons bien reçu votre demande de devis concernant : ${detail.product_type || 'votre projet'}.%0A%0ACordialement,%0AL'équipe Artimarq`}
              className="btn btn-primary btn-md"
            >📧 Répondre par email</a>
          </div>
        )}
      </Modal>

      <ConfirmDialog isOpen={!!confirm} onClose={() => setConfirm(null)} onConfirm={() => deleteOrder(confirm)} title="Supprimer la commande" message="Cette action est irréversible." confirmLabel="Supprimer" danger />
      <ConfirmDialog isOpen={!!confirmQQ} onClose={() => setConfirmQQ(null)} onConfirm={deleteQuickQuote} title="Supprimer la demande de devis" message="Cette demande sera définitivement supprimée. Cette action est irréversible." confirmLabel="Supprimer" danger />
    </div>
  );
};

/* ════════════════════════════════════════════════════
   QUOTES PAGE
   ════════════════════════════════════════════════════ */
export const AdminQuotesPage = () => {
  const [quotes,  setQuotes]  = useState([]);
  const [meta,    setMeta]    = useState(null);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState('');
  const [status,  setStatus]  = useState('');
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);

  const fetch = useCallback(()=>{
    setLoading(true);
    const p={page,per_page:20};
    if(search)p.search=search;
    if(status)p.status=status;
    quoteAPI.list(p).then(({data})=>{setQuotes(data.data||[]);setMeta(data.meta);}).catch(()=>{}).finally(()=>setLoading(false));
  },[page,search,status]);

  useEffect(()=>{fetch();},[fetch]);

  const updateStatus = async (id, s) => {
    try { await quoteAPI.updateStatus(id,{status:s}); toast.success('Mis à jour'); fetch(); } catch { toast.error('Erreur'); }
  };

  const convertToInvoice = async (id) => {
    try { const {data}=await quoteAPI.convert(id); toast.success(`Facture ${data.data.reference} créée`); fetch(); } catch(err){ toast.error(err.response?.data?.message||'Erreur'); }
  };

  const deleteQuote = async (id) => {
    try { await quoteAPI.delete(id); toast.success('Devis supprimé'); fetch(); } catch { toast.error('Erreur'); }
    setConfirm(null);
  };

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1}}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Devis</h1><p className="admin-page-subtitle">Gestion de tous les devis clients</p></div>
        <Button variant="primary" onClick={()=>window.location.href='/admin/quotes/new'}>+ Nouveau devis</Button>
      </div>
      <div className="admin-page-content">
        <div className="admin-card">
          <div className="admin-card-header">
            <div className="admin-filters">
              <div className="search-bar">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input className="form-input" placeholder="Référence, client…" value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} style={{paddingLeft:36}}/>
              </div>
              <select className="filter-select" value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
                <option value="">Tous les statuts</option>
                {['pending','sent','accepted','rejected','expired','converted'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {loading ? <div className="page-loader"><div className="spinner"/></div> :
            !quotes.length ? <EmptyState icon="📋" title="Aucun devis"/> :
            <div style={{overflowX:'auto'}}>
              <table className="data-table">
                <thead><tr><th>Référence</th><th>Client</th><th>Total TTC</th><th>Statut</th><th>Valide jusqu'au</th><th>Créé le</th><th></th></tr></thead>
                <tbody>
                  {quotes.map(q=>(
                    <tr key={q.id}>
                      <td><code style={{fontSize:12,background:'var(--bg-page)',padding:'2px 6px',borderRadius:4}}>{q.reference}</code></td>
                      <td style={{fontWeight:600}}>{q.client_snapshot?.company||q.client_snapshot?.contact_name||q.client_company||'—'}</td>
                      <td style={{fontWeight:700,color:'var(--brand-accent)'}}>{parseFloat(parseFloat(q.total_ttc) || 0).toFixed(2)} €</td>
                      <td><StatusBadge status={q.status}/></td>
                      <td style={{fontSize:13,color:'var(--text-muted)'}}>{q.valid_until?new Date(q.valid_until).toLocaleDateString('fr-FR'):'—'}</td>
                      <td style={{fontSize:12,color:'var(--text-muted)'}}>{new Date(q.created_at).toLocaleDateString('fr-FR')}</td>
                      <td>
                        <div style={{display:'flex',gap:6}}>
                          <a href={quoteAPI.pdfUrl(q.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="secondary">PDF</Button></a>
                          {q.status==='accepted'&&<Button size="sm" variant="primary" onClick={()=>convertToInvoice(q.id)}>→ Facture</Button>}
                          {q.status==='pending'&&<Button size="sm" variant="ghost" onClick={()=>updateStatus(q.id,'sent')}>Marquer envoyé</Button>}
                          <Button size="sm" variant="ghost" onClick={()=>setConfirm(q.id)}>🗑</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
          <div style={{padding:'var(--space-md) var(--space-lg)',borderTop:'1px solid var(--border)'}}>
            <Pagination meta={meta} onPageChange={setPage}/>
          </div>
        </div>
      </div>
      <ConfirmDialog isOpen={!!confirm} onClose={()=>setConfirm(null)} onConfirm={()=>deleteQuote(confirm)} title="Supprimer le devis" message="Cette action est irréversible." confirmLabel="Supprimer" danger/>
    </div>
  );
};

/* ════════════════════════════════════════════════════
   INVOICES PAGE
   ════════════════════════════════════════════════════ */
export const AdminInvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [meta,     setMeta]     = useState(null);
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState('');
  const [status,   setStatus]   = useState('');
  const [loading,  setLoading]  = useState(true);
  const [payModal, setPayModal] = useState(null);
  const [payForm,  setPayForm]  = useState({amount:'',method:'bank_transfer',reference:'',paid_at:new Date().toISOString().split('T')[0],notes:''});
  const [paying,   setPaying]   = useState(false);

  const fetch = useCallback(()=>{
    setLoading(true);
    const p={page,per_page:20};
    if(search)p.search=search;
    if(status)p.status=status;
    invoiceAPI.list(p).then(({data})=>{setInvoices(data.data||[]);setMeta(data.meta);}).catch(()=>{}).finally(()=>setLoading(false));
  },[page,search,status]);

  useEffect(()=>{fetch();},[fetch]);

  const addPayment = async () => {
    if (!payForm.amount) return;
    setPaying(true);
    try {
      await invoiceAPI.addPayment(payModal.id, payForm);
      toast.success('Règlement enregistré');
      setPayModal(null);
      setPayForm({amount:'',method:'bank_transfer',reference:'',paid_at:new Date().toISOString().split('T')[0],notes:''});
      fetch();
    } catch (err) { toast.error(err.response?.data?.message||'Erreur'); }
    finally { setPaying(false); }
  };

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1}}>
      <div className="admin-page-header">
        <div><h1 className="admin-page-title">Factures</h1><p className="admin-page-subtitle">Suivi de la facturation et des règlements</p></div>
      </div>
      <div className="admin-page-content">
        <div className="admin-card">
          <div className="admin-card-header">
            <div className="admin-filters">
              <div className="search-bar">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input className="form-input" placeholder="Référence, client…" value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} style={{paddingLeft:36}}/>
              </div>
              <select className="filter-select" value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
                <option value="">Tous les statuts</option>
                {['draft','sent','paid','partial','overdue','cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {loading ? <div className="page-loader"><div className="spinner"/></div> :
            !invoices.length ? <EmptyState icon="🧾" title="Aucune facture"/> :
            <div style={{overflowX:'auto'}}>
              <table className="data-table">
                <thead><tr><th>Référence</th><th>Client</th><th>Total TTC</th><th>Payé</th><th>Solde dû</th><th>Statut</th><th>Échéance</th><th></th></tr></thead>
                <tbody>
                  {invoices.map(inv=>(
                    <tr key={inv.id}>
                      <td><code style={{fontSize:12,background:'var(--bg-page)',padding:'2px 6px',borderRadius:4}}>{inv.reference}</code></td>
                      <td style={{fontWeight:600}}>{inv.client_snapshot?.company||inv.client_company||'—'}</td>
                      <td style={{fontWeight:700}}>{parseFloat(parseFloat(inv.total_ttc) || 0).toFixed(2)} €</td>
                      <td style={{color:'var(--success)',fontWeight:600}}>{parseFloat(parseFloat(inv.amount_paid) || 0).toFixed(2)} €</td>
                      <td style={{color:parseFloat(inv.balance_due||0)>0?'var(--error)':'var(--success)',fontWeight:700}}>{parseFloat(parseFloat(inv.balance_due) || 0).toFixed(2)} €</td>
                      <td><StatusBadge status={inv.status}/></td>
                      <td style={{fontSize:12,color:new Date(inv.due_date)<new Date()&&inv.status!=='paid'?'var(--error)':'var(--text-muted)'}}>{inv.due_date?new Date(inv.due_date).toLocaleDateString('fr-FR'):'—'}</td>
                      <td>
                        <div style={{display:'flex',gap:6}}>
                          <a href={invoiceAPI.pdfUrl(inv.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="secondary">PDF</Button></a>
                          {inv.status!=='paid'&&parseFloat(inv.balance_due||0)>0&&<Button size="sm" variant="primary" onClick={()=>{setPayModal(inv);setPayForm(p=>({...p,amount:parseFloat(inv.balance_due).toFixed(2)}))}}>💶 Solder</Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
          <div style={{padding:'var(--space-md) var(--space-lg)',borderTop:'1px solid var(--border)'}}>
            <Pagination meta={meta} onPageChange={setPage}/>
          </div>
        </div>
      </div>

      <Modal isOpen={!!payModal} onClose={()=>setPayModal(null)} title={`Enregistrer un règlement — ${payModal?.reference||''}`}
        footer={<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}><Button variant="ghost" onClick={()=>setPayModal(null)}>Annuler</Button><Button variant="primary" loading={paying} onClick={addPayment}>Enregistrer</Button></div>}
      >
        {payModal&&(
          <div style={{display:'flex',flexDirection:'column',gap:'var(--space-md)'}}>
            <div style={{padding:'var(--space-md)',background:'var(--bg-page)',borderRadius:'var(--radius-sm)',display:'flex',justifyContent:'space-between',fontSize:14}}>
              <span>Solde dû</span><strong style={{color:'var(--error)'}}>{parseFloat(parseFloat(payModal.balance_due) || 0).toFixed(2)} €</strong>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'var(--space-md)'}}>
              <div className="form-field"><label className="form-label">Montant (€)</label><input type="number" step="0.01" className="form-input" value={payForm.amount} onChange={e=>setPayForm(p=>({...p,amount:e.target.value}))}/></div>
              <div className="form-field"><label className="form-label">Date</label><input type="date" className="form-input" value={payForm.paid_at} onChange={e=>setPayForm(p=>({...p,paid_at:e.target.value}))}/></div>
              <div className="form-field"><label className="form-label">Mode de paiement</label>
                <select className="form-input form-select" value={payForm.method} onChange={e=>setPayForm(p=>({...p,method:e.target.value}))}>
                  {[['bank_transfer','Virement'],['check','Chèque'],['cash','Espèces'],['card','Carte'],['other','Autre']].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="form-field"><label className="form-label">Référence</label><input className="form-input" value={payForm.reference} onChange={e=>setPayForm(p=>({...p,reference:e.target.value}))} placeholder="N° chèque, virement…"/></div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};